import { StatusData } from "@fairfleet/geotab";
import { useMemo } from "react";
import { LiveDataRow } from "../models/LiveDataRow";
import { toLiveDataRow } from "../models/toLiveDataRow";
import { useLiveDataLookups } from "./useLiveDataLookups";
import { useLiveStatusData } from "./useLiveStatusData";
import { useDayStatusData } from "@/hooks/statusData/useDayStatusData";

export function useLiveDataRows(
  deviceId: string | undefined,
  minutesBack: number = 5,
  refetchInterval: number = 10000,
) {
  const {
    data: statusData,
    isLoading,
    isFetching,
  } = useLiveStatusData(deviceId, minutesBack, refetchInterval);
  // What the checklist falls back to when the live window has nothing: the last 24 hours.
  const { data: fallbackStatusData } = useDayStatusData(deviceId);

  // Sorted and deduplicated, because this array is part of the diagnostics query key. Left in
  // arrival order it changes whenever a rarely reported diagnostic enters or leaves the sliding
  // live window, and every diagnostic on the vehicle is fetched again.
  const diagnosticIds = useMemo(
    () => sortedDiagnosticIds(statusData, fallbackStatusData),
    [statusData, fallbackStatusData],
  );

  const { lookups, isLoading: diagnosticsLoading } = useLiveDataLookups(diagnosticIds);

  const rows: LiveDataRow[] = useMemo(
    () => (statusData ?? []).map(sd => toLiveDataRow(sd, lookups)),
    [statusData, lookups],
  );

  const fallbackRows: LiveDataRow[] = useMemo(
    () => (fallbackStatusData ?? []).map(sd => toLiveDataRow(sd, lookups)),
    [fallbackStatusData, lookups],
  );

  return { rows, fallbackRows, isLoading, isFetching, diagnosticsLoading };
}

function sortedDiagnosticIds(...pages: (StatusData[] | undefined)[]): string[] {
  const ids = new Set<string>();

  for (const page of pages) {
    for (const record of page ?? []) {
      const id = record.diagnostic?.id;
      if (id) {
        ids.add(id);
      }
    }
  }

  return [...ids].sort();
}
